/**
 * FHIR extension URLs, code systems, and constants for the intake flow.
 * Keeps intake-specific FHIR conventions in one place.
 */

import type { ScreeningField } from './types';

// ---------------------------------------------------------------------------
// Extension URLs (Practitioner profile enrichment for intake display)
// ---------------------------------------------------------------------------

export const INTAKE_EXT_URLS = {
  credentials: 'https://unify.health/fhir/credentials',
  shortBio: 'https://unify.health/fhir/short-bio',
  longBio: 'https://unify.health/fhir/long-bio',
  headshotUrl: 'https://unify.health/fhir/headshot-url',
  sessionFormats: 'https://unify.health/fhir/session-formats',
  acceptsInsurance: 'https://unify.health/fhir/accepts-insurance',
  takingNewClients: 'https://unify.health/fhir/taking-new-clients',
  intakeProvider: 'https://unify.health/fhir/intake-provider',
} as const;

// ---------------------------------------------------------------------------
// Questionnaire canonical URLs for intake
// ---------------------------------------------------------------------------

/** Screening-only intake questionnaire (six yes/no clinical items). */
export const INTAKE_SCREENING_QUESTIONNAIRE_URL =
  'https://unify.health/fhir/Questionnaire/client-intake-screening';

export interface ScreeningQuestionDef {
  /** QuestionnaireResponse item linkId (must match buildScreeningQuestionnaireItems). */
  linkId: string;
  /** IntakeAnswers field key. */
  field: ScreeningField;
  /** Full client-facing question wording. */
  label: string;
}

/**
 * Canonical definitions for the six intake screening questions: the QuestionnaireResponse
 * item linkId, the IntakeAnswers field, and the client-facing wording. Single source of truth
 * for both the intake form (StepScreening) and any screening-results display.
 */
export const SCREENING_QUESTIONS: readonly ScreeningQuestionDef[] = [
  {
    linkId: 'screening-emergency',
    field: 'screeningEmergency',
    label: 'Are you currently experiencing an emergency or crisis situation?',
  },
  {
    linkId: 'screening-hospitalization',
    field: 'screeningHospitalization',
    label: 'Have you ever received care in a hospital for mental health concerns?',
  },
  {
    linkId: 'screening-suicidal-ideation',
    field: 'screeningSuicidalIdeation',
    label: 'Are you currently experiencing suicidal ideations?',
  },
  {
    linkId: 'screening-medications',
    field: 'screeningMedications',
    label: 'Are you currently taking any prescription mental health medication?',
  },
  {
    linkId: 'screening-substance-use',
    field: 'screeningSubstanceUse',
    label: 'Are you concerned about your current level of alcohol or drug use?',
  },
  {
    linkId: 'screening-legal',
    field: 'screeningLegalProceedings',
    label: 'Are you currently involved in any legal proceedings?',
  },
] as const;

// ---------------------------------------------------------------------------
// Slot / Schedule service type for intake vs consultation
// ---------------------------------------------------------------------------

export const INTAKE_SERVICE_TYPE_SYSTEM = 'https://unify.health/fhir/intake-service-type';

export const INTAKE_SERVICE_TYPES = {
  intake: { system: INTAKE_SERVICE_TYPE_SYSTEM, code: 'intake', display: 'Intake Session (50 min)' },
  consultation: { system: INTAKE_SERVICE_TYPE_SYSTEM, code: 'consultation', display: 'Consultation (15 min)' },
} as const;

// ---------------------------------------------------------------------------
// Appointment status mapping
// ---------------------------------------------------------------------------

export const INTAKE_APPOINTMENT_STATUS = 'booked' as const;
